import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { stringResources } from 'src/assets/config/stringResources';
import { RootState } from 'src/redux/store';
import { TripCostRequest, TripCostResponse, TripCostsClient } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';
import { CostsDataModel } from 'src/views/fishingTrip/Import/DataTypes';

export interface TripCost extends TripCostResponse { }

export interface State {
    isLoading: boolean;
    error: boolean;
    tripCostList: TripCost[];
}

const initialState: State = {
    isLoading: false,
    error: false,
    tripCostList: [],
};

const tripCostSlice = createSlice({
    name: 'tripCost',
    initialState,
    reducers: {
        startLoading: (state) => {
            state.isLoading = true;
        },
        hasError: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
        },
        getTripCostListSuccess: (state, action) => {
            state.tripCostList = action.payload;
            state.isLoading = false;
        },
        addNewTripCostSuccess: (state, action: PayloadAction<TripCost>) => {
            const newItem = action.payload;
            state.tripCostList = [newItem, ...state.tripCostList];
            toast.success(stringResources.REDUX_SLICE.CREATE_SUCCESS);
            state.isLoading = false;
        },
        updateTripCostSuccess: (state, action: PayloadAction<TripCost>) => {
            const updatedItem = action.payload;
            const index = state.tripCostList.findIndex(item => item.tripCostId === updatedItem.tripCostId);
            state.tripCostList = [...state.tripCostList.slice(0, index), updatedItem, ...state.tripCostList.slice(index + 1)];
            toast.success(stringResources.REDUX_SLICE.UPDATE_SUCCESS);
            state.isLoading = false;
        },
        deleteTripCostSuccess: (state, action: PayloadAction<number>) => {
            const deleteId = action.payload;
            state.tripCostList = state.tripCostList.filter(item => item.tripCostId !== deleteId);
            state.isLoading = false;
        },
        distributeTripCostSuccess: (state, action) => {
            const newTripCosts = action.payload;
            state.tripCostList = [...newTripCosts, ...state.tripCostList];
            toast.success(stringResources.REDUX_SLICE.DISTRIBUTE_SUCCESS);
            state.isLoading = false;
        },
    },
});

// Reducer
export default tripCostSlice.reducer

// Actions
export const {
    startLoading,
    hasError,
    getTripCostListSuccess,
} = tripCostSlice.actions;

// for useSelector
export const selectTripCost = (state: RootState) => state.tripCost as State;

// ----------------------------------------------------------------------
export function getAllTripCost(fishingTripId: number) {
    return async (dispatch, getState) => {
        dispatch(tripCostSlice.actions.startLoading());
        try {
            const client = new TripCostsClient(undefined, axios);
            const response = await client.getByFishingTrip(fishingTripId);
            dispatch(tripCostSlice.actions.getTripCostListSuccess(response));
            return response;
        } catch (error) {
            dispatch(tripCostSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function createTripCost(tripCost: TripCost) {
    return async (dispatch, getState) => {
        dispatch(tripCostSlice.actions.startLoading());
        try {
            const request = {
                fishingTripPeriodId: tripCost.fishingTripPeriodId,
                description: tripCost.description,
                quantity: tripCost.quantity,
                rate: tripCost.rate,
            } as TripCostRequest;
            const client = new TripCostsClient(undefined, axios);
            const response = await client.create(request);
            dispatch(tripCostSlice.actions.addNewTripCostSuccess(response));
        } catch (error) {
            dispatch(tripCostSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function updateTripCost(tripCost: TripCost) {
    return async (dispatch, getState) => {
        dispatch(tripCostSlice.actions.startLoading());
        try {
            const request = {
                fishingTripPeriodId: tripCost.fishingTripPeriodId,
                description: tripCost.description,
                quantity: tripCost.quantity,
                rate: tripCost.rate,
            } as TripCostRequest;
            const client = new TripCostsClient(undefined, axios);
            const response = await client.edit(tripCost.tripCostId, request);
            dispatch(tripCostSlice.actions.updateTripCostSuccess(response));
        } catch (error) {
            dispatch(tripCostSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function deleteTripCost(tripCostId: number, showNotification: boolean = true) {
    return async (dispatch, getState) => {
        dispatch(tripCostSlice.actions.startLoading());
        try {
            const client = new TripCostsClient(undefined, axios);
            await client.delete(tripCostId);
            dispatch(tripCostSlice.actions.deleteTripCostSuccess(tripCostId));
            if (showNotification) {
                toast.success(stringResources.REDUX_SLICE.DELETE_SUCCESS);
            }
            return true;
        } catch (error) {
            dispatch(tripCostSlice.actions.hasError(true));
            handleError(error);
            return false;
        }
    };
}

export function distributeTripCost(fishingTripId: number, tripCost: TripCost) {
    return async (dispatch, getState) => {
        dispatch(tripCostSlice.actions.startLoading());
        try {
            const client = new TripCostsClient(undefined, axios);
            const response = await client.splitCostsPerPeriods(fishingTripId, tripCost.description, tripCost.quantity, tripCost.rate)
            dispatch(tripCostSlice.actions.distributeTripCostSuccess(response));
        } catch (error) {
            dispatch(tripCostSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function createTripCostsBatch(data: CostsDataModel[], periodIdMap: Map<string, number>) {
    return async (dispatch, getState) => {
        try {
            const request: TripCostRequest[] = [];
            for (const d of data) {
                const fishingTripPeriodId = periodIdMap.get(d.Period ?? '');
                if (fishingTripPeriodId) {
                    request.push({
                        fishingTripPeriodId,
                        description: d.Description ?? '',
                        quantity: d.Quantity ?? 0,
                        rate: d.Rate ?? 0,
                    } as TripCostRequest);
                }
            }
            const client = new TripCostsClient(undefined, axios);
            await client.createBatch(request);
        } catch (error) {
            handleError(error);
        }
    };
}
